import React, { useMemo } from 'react';

import { Stack } from 'react-bootstrap';
import ReactECharts from 'echarts-for-react';

import { translateComponent } from '@/utils/tolgee/translateComponent';

interface LomavisHeatMap1Props {
  data: Array<{ title: string; data: Record<number, number> }>;
  metric?: string;
}

/**
 * Atoms: LomavisHeatMap1
 * Expecting to render a scatter plot of day vs time with plots indicating values
 */
export const LomavisHeatMap1: React.FC<LomavisHeatMap1Props> = ({
  data,
  metric
}) => {
  const t = translateComponent('LomavisHeatMap1');

  const hours = [
    '00:00',
    '01:00',
    '02:00',
    '03:00',
    '04:00',
    '05:00',
    '06:00',
    '07:00',
    '08:00',
    '09:00',
    '10:00',
    '11:00',
    '12:00',
    '13:00',
    '14:00',
    '15:00',
    '16:00',
    '17:00',
    '18:00',
    '19:00',
    '20:00',
    '21:00',
    '22:00',
    '23:00'
  ];

  //   const hours = Array.from(new Set(data.flatMap((d) => Object.keys(d.data))))
  //     .map((h) => String(h).padStart(2, '0') + ':00')
  //     .sort();
  const days = data.map((day) => t(day.title.toLowerCase()));
  console.log('days', days);

  const convertDataToScatter = (
    inputData: Array<{ title: string; data: Record<number, number> }>
  ) => {
    const result: Array<[number, number, number]> = [];
    let maxValue = 0;
    let peakDay = '';
    let peakHour = '';
    let peakValue = 0;

    inputData.forEach((dayObj, dayIndex) => {
      for (let hour = 0; hour < 24; hour++) {
        const value = Number(dayObj.data[hour]) || 0;
        // Always include 0 so small dot is visible
        result.push([hour, dayIndex, value]);
        maxValue = Math.max(maxValue, value);
        if (value > peakValue) {
          peakValue = value;
          peakDay = days[dayIndex];
          peakHour = hours[hour];
        }
      }
    });

    return { data: result, maxValue, peakDay, peakHour, peakValue };
  };
  const { data: chartData, maxValue } = useMemo(
    () => convertDataToScatter(data),
    [data]
  );

  const BOOTSTRAP_PRIMARY_RANGE_COLORS = [
    { max: 0.3, color: '#b6d4fe' }, // lightest primary
    { max: 0.6, color: '#7fb2fd' }, // lighter primary
    { max: 0.8, color: '#3d8bfd' }, // medium primary
    { max: 1, color: '#0d6efd' } // default primary
  ];

  const option = useMemo(
    () => ({
      tooltip: {
        position: 'top',
        formatter: (params) => {
          const hour = String(hours[params.data[0]]);
          const day = String(days[params.data[1]]);
          const value = String(params.data[2]);
          return `${value} ${metric ?? 'posts'}<br/>${day} at ${hour}`;
        }
      },
      grid: { left: 80, right: 20, top: 20, bottom: 40 },
      xAxis: {
        type: 'category',
        data: hours,
        axisLabel: {
          formatter: (value, index) => (index % 2 === 0 ? value : ''),
          fontSize: 11,
          color: '#6c757d'
        },
        axisTick: { show: false },
        axisLine: { show: false },
        splitLine: { show: false }
      },
      yAxis: {
        type: 'category',
        inverse: true,
        data: days,
        axisLabel: { fontSize: 11, color: '#6c757d' },
        axisTick: { show: false },
        axisLine: { show: false },
        splitLine: { show: false }
      },
      series: [
        {
          name: 'Posts',
          type: 'scatter',
          data: chartData,
          symbolSize: (data) => {
            if (maxValue === 0) return 4;
            const minSize = 4;
            const maxSize = 30;
            return minSize + (maxSize - minSize) * (data[2] / maxValue);
          },
          itemStyle: {
            color: (params) => {
              const ratio = maxValue === 0 ? 0 : params.data[2] / maxValue;
              const shade = BOOTSTRAP_PRIMARY_RANGE_COLORS.find(
                (s) => ratio <= s.max
              );
              return shade
                ? shade.color
                : BOOTSTRAP_PRIMARY_RANGE_COLORS.at(-1).color;
            },
            opacity: 0.9
          },
          emphasis: {
            itemStyle: {
              color: BOOTSTRAP_PRIMARY_RANGE_COLORS[3].color,
              opacity: 1,
              borderColor: '#fff',
              borderWidth: 2
            }
          }
        }
      ]
    }),
    [chartData, maxValue]
  );

  return (
    <Stack>
      <div className=" mb-4">
        <div className="">
          {/* Header */}

          {/* Chart */}
          <ReactECharts
            option={option}
            style={{ height: '280px', width: '100%' }}
          />

          {/* Legend */}
          <div className="d-flex justify-content-end align-items-center gap-3 mt-3 small">
            <div className="d-flex align-items-center gap-1">
              <div
                className="rounded-circle"
                style={{
                  width: '16px',
                  height: '16px',
                  backgroundColor: BOOTSTRAP_PRIMARY_RANGE_COLORS[3].color,
                  opacity: 0.9
                }}
              ></div>
              <span className="fw-semibold">
                {t('more')} {metric ?? t('posts')}
              </span>
            </div>
            <div className="d-flex align-items-center gap-1">
              <div
                className="rounded-circle"
                style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: BOOTSTRAP_PRIMARY_RANGE_COLORS[0].color,
                  opacity: 0.9
                }}
              ></div>
              <span className="fw-semibold">
                {t('less')} {metric ?? t('posts')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Stack>
  );
};
